import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { socketService, setSocketDispatch } from '@/services/socketService';

export interface SocketConnectionState {
    isConnected: boolean;
    connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error';
    reconnectAttempts: number;
    lastConnectedAt?: Date;
    error?: string;
}

// Global socket initialization tracking to prevent multiple simultaneous connections
let globalSocketInitialized = false;
let globalInitPromise: Promise<void> | null = null;
let socketInstanceCount = 0;

export const useSocket = () => {
    const dispatch = useDispatch();
    const { token, user } = useSelector((state: RootState) => state.auth);
    const [connectionState, setConnectionState] = useState<SocketConnectionState>({
        isConnected: false,
        connectionStatus: 'disconnected',
        reconnectAttempts: 0
    });

    const instanceIdRef = useRef<number>(0);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize socket connection when user is authenticated
    useEffect(() => {
        if (!token || !user) return;

        // Increment instance counter and assign ID
        socketInstanceCount++;
        instanceIdRef.current = socketInstanceCount;

        console.log(`Socket hook instance ${instanceIdRef.current} initializing for user:`, user._id);

        // If socket is already being initialized, wait for it
        if (globalInitPromise) {
            globalInitPromise.then(() => {
                console.log(`Socket hook instance ${instanceIdRef.current} using existing connection`);
            });
            return;
        }

        // If socket is not initialized, initialize it (only first instance does this)
        if (!globalSocketInitialized) {
            console.log(`Socket hook instance ${instanceIdRef.current} creating new connection`);
            globalSocketInitialized = true;

            globalInitPromise = (async () => {
                try {
                    // Set the dispatch function for the socket service
                    setSocketDispatch(dispatch);

                    // Connect to socket
                    socketService.connect(token, user._id);
                } catch (error) {
                    console.error('Socket initialization error:', error);
                    globalSocketInitialized = false;
                    globalInitPromise = null;
                }
            })();
        } else {
            console.log(`Socket hook instance ${instanceIdRef.current} using existing connection`);
        }

        return () => {
            socketInstanceCount--;
            console.log(`Socket hook instance ${instanceIdRef.current} unmounting, remaining instances: ${socketInstanceCount}`);

            // Only disconnect when the last instance is unmounting
            if (socketInstanceCount <= 0) {
                console.log('Last socket instance unmounting, disconnecting socket');
                socketService.disconnect();
                globalSocketInitialized = false;
                globalInitPromise = null;
                socketInstanceCount = 0; // Reset counter to prevent negative values
            }
        };
    }, [token, user, dispatch]);

    // Monitor connection state
    useEffect(() => {
        const checkConnectionState = () => {
            const status = socketService.getConnectionStatus();
            const connected = socketService.isConnected();

            setConnectionState(prev => ({
                ...prev,
                isConnected: connected,
                connectionStatus: status,
                lastConnectedAt: connected && !prev.isConnected ? new Date() : prev.lastConnectedAt
            }));
        };

        // Check immediately
        checkConnectionState();

        // Set up interval to check connection state
        const interval = setInterval(checkConnectionState, 2000);

        return () => clearInterval(interval);
    }, []);

    // Clean up on component unmount
    useEffect(() => {
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    // Manual reconnect function
    const reconnect = () => {
        if (token && user) {
            console.log('Manual reconnect requested');
            socketService.disconnect();

            // Wait a bit before reconnecting
            reconnectTimeoutRef.current = setTimeout(() => {
                socketService.connect(token, user._id);
            }, 1000);
        }
    };

    // Force disconnect
    const disconnect = () => {
        console.log('Manual disconnect requested');
        socketService.disconnect();
    };

    return {
        connectionState,
        reconnect,
        disconnect,
        socket: socketService
    };
};